import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, Ambulance, ArrowRight, Shield, Clock, Star, Mail, Phone } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import { setPassengerSession } from "../../passengerAuth";
import "./Passenger.css";

export default function PassengerLogin() {
  const navigate = useNavigate();

  // "email" | "phone"
  const [loginMode,    setLoginMode]    = useState("email");
  const [emailInput,   setEmailInput]   = useState("");
  const [phoneInput,   setPhoneInput]   = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const identifier =
      loginMode === "email"
        ? emailInput.trim()
        : phoneInput.startsWith("+") ? phoneInput : `+${phoneInput}`;

    if (!identifier || !password) {
      setError("Please enter your email/phone and password.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/passengerLoginWeb`,
        { identifier, password }
      );

      const token         = res?.data?.accessToken;
      const passengerData = res?.data?.passengerData;

      if (!token || !passengerData) {
        setError("Login failed. Please check your credentials.");
        setLoading(false);
        return;
      }

      setPassengerSession({ token, passengerData });
      navigate("/passenger");
    } catch (err) {
      const errData = err?.response?.data;
      if (errData?.requiresVerification) {
        navigate(`/passenger/verify-email?email=${encodeURIComponent(errData.email)}`);
        return;
      }

      const msg =
        errData?.msg ||
        errData?.message ||
        "Invalid credentials. Please try again.";
      setError(msg);
      setLoading(false);
    }
  };

  const isValid = password && (loginMode === "email" ? emailInput : phoneInput);

  return (
    <div className="pax-login-wrapper">
      {/* LEFT PANEL */}
      <div className="pax-login-left">
        <div className="pax-login-left-content">
          <div className="pax-brand-icon">
            <Ambulance size={40} color="#fff" />
          </div>
          <h1 className="pax-brand-title">ለAmbulance</h1>
          <p className="pax-brand-subtitle">Passenger Portal</p>

          <div className="pax-features">
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><Ambulance size={18} /></div>
              <div>
                <div className="pax-feature-label">Emergency Response</div>
                <div className="pax-feature-desc">Immediate medical assistance</div>
              </div>
            </div>
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><Clock size={18} /></div>
              <div>
                <div className="pax-feature-label">Transport Rides</div>
                <div className="pax-feature-desc">Safe, reliable medical transport</div>
              </div>
            </div>
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><Star size={18} /></div>
              <div>
                <div className="pax-feature-label">Real-Time Tracking</div>
                <div className="pax-feature-desc">Live updates on your request</div>
              </div>
            </div>
          </div>

          <div className="pax-emergency-badge">
            <span className="pax-badge-dot" />
            Emergency Hotline: <strong>9117</strong>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="pax-login-right">
        <div className="pax-login-form-wrapper">
          <div className="pax-form-header">
            <h2>Welcome Back</h2>
            <p>Sign in with your email or phone number</p>
          </div>

          {/* Login mode toggle */}
          <div className="pax-login-mode-toggle">
            <button
              type="button"
              className={`pax-login-mode-btn ${loginMode === "email" ? "active" : ""}`}
              onClick={() => { setLoginMode("email"); setError(""); }}
              id="pax-login-email-tab"
            >
              <Mail size={15} /> Email
            </button>
            <button
              type="button"
              className={`pax-login-mode-btn ${loginMode === "phone" ? "active" : ""}`}
              onClick={() => { setLoginMode("phone"); setError(""); }}
              id="pax-login-phone-tab"
            >
              <Phone size={15} /> Phone
            </button>
          </div>

          {error && (
            <div className="pax-error-alert">
              <span>⚠</span> {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="pax-form">
            {/* Email or phone field */}
            {loginMode === "email" ? (
              <div className="pax-form-group">
                <label>Email Address</label>
                <div className="pax-input-wrapper">
                  <span className="pax-input-icon"><Mail size={17} /></span>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    id="pax-login-email"
                  />
                </div>
              </div>
            ) : (
              <div className="pax-form-group">
                <label>Phone Number</label>
                <PhoneInput
                  country="et"
                  value={phoneInput}
                  onChange={(value) => setPhoneInput(value)}
                  enableAreaCodes={true}
                  inputProps={{
                    id: "pax-login-phone",
                    name: "phone",
                    autoComplete: "username",
                    required: true,
                  }}
                  containerClass="pax-phone-container"
                  inputClass="pax-phone-input"
                  buttonClass="pax-phone-flag-btn"
                />
              </div>
            )}

            {/* Password */}
            <div className="pax-form-group">
              <label>Password</label>
              <div className="pax-input-wrapper">
                <span className="pax-input-icon"><Lock size={17} /></span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  autoComplete="current-password"
                  id="pax-login-password"
                />
                <button
                  type="button"
                  className="pax-eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="pax-submit-btn"
              disabled={loading || !isValid}
              id="pax-login-btn"
            >
              {loading ? (
                <span className="pax-btn-loading">
                  <span className="pax-spinner" /> Signing in…
                </span>
              ) : (
                <span className="pax-btn-content">
                  Sign In <ArrowRight size={18} />
                </span>
              )}
            </button>
          </form>

          <div className="pax-form-footer">
            <div className="pax-divider"><span>New to the portal?</span></div>
            <p style={{ textAlign: "center", marginTop: 12, fontSize: "0.9rem", color: "#475569" }}>
              Don't have an account?{" "}
              <Link
                to="/passenger/signup"
                style={{ color: "#1d4ed8", fontWeight: 600, textDecoration: "none" }}
                id="pax-go-signup"
              >
                Create one here
              </Link>
            </p>
            <p style={{ textAlign: "center", marginTop: 6, fontSize: "0.8rem", color: "#94a3b8" }}>
              Mobile app users can sign in with their phone number.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
